import React, { CSSProperties, useRef } from 'react';
import { CheckboxComponent } from '@vivli/shared/components';
import { IOrganization, IOrganizationMember } from '@vivli/features/organizations/infrastructure/interface';
import { ColDef, GridApi } from 'ag-grid-community';
import { NameRowRendererView } from './name-row-renderer.view';
import { OrgMemberCheckBoxRendererView } from './org-member-checkBox-renderer.view';
import { GridView } from '@vivli/shared/views';
import { IVivliRoleDef } from '@vivli/shared/infrastructure/interface';
import { Color, Size, Styles } from '@vivli/shared/theme';
import './membership.styles.scss';
import { useOrganizationDetailsContext } from '@vivli/features/organizations/infrastructure/context';

interface OrgMembersGridViewProps {
    currentOrgMembers?: IOrganizationMember[];
    organization?: IOrganization;
    rolesOrgSpecific?: IVivliRoleDef[];
    isUserDetails?: boolean;
}

const checkboxStyle: CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: 0,
};
export const OrgMembersGridView = ({
    currentOrgMembers,
    organization,
    rolesOrgSpecific,
    isUserDetails = false,
}: OrgMembersGridViewProps) => {
    //Default roles are hard-coded
    const defaultRoles = ['OrgAdmin', 'DataContrib', 'IrpReviewer', 'Curator', 'HeadCurator', 'CuratorQc'];
    const orgId = isUserDetails ? 'N/A' : organization?.id;
    const hasChanges = !isUserDetails && currentOrgMembers && currentOrgMembers.filter((mem) => mem.wasUpdated).length > 0;
    let handleOnGridReady = (gridApi: GridApi) => {};
    if (!isUserDetails) {
        const { membersGridApiRef } = useOrganizationDetailsContext();
        handleOnGridReady = (gridApi: GridApi) => {
            if (!isUserDetails) {
                membersGridApiRef.current = gridApi;
            }
        };
    }
    const columnDefs: ColDef[] = [
        {
            field: 'displayName',
            headerName: 'Name',
            sortable: true,
            filter: true,
            cellRenderer: 'NameRow',
            flex: 2,
        },
        {
            field: 'hasRoleOrgAdmin',
            headerName: 'Org Admin',
            sortable: true,
            filter: true,
            cellStyle: { ...checkboxStyle },
            cellRenderer: 'MemberCheckbox',
            cellRendererParams: {
                orgId: orgId,
            },
            flex: 1,
        },
    ];

    if (isUserDetails || organization.isDataProvider) {
        columnDefs.push({
            field: 'hasRoleDataContrib',
            headerName: 'Data Uploader',
            sortable: true,
            filter: true,
            cellStyle: { ...checkboxStyle },
            cellRenderer: 'MemberCheckbox',
            cellRendererParams: {
                orgId: orgId,
            },
            flex: 1,
        });
    }
    if (isUserDetails || organization.isIRPApprover) {
        columnDefs.push({
            field: 'hasRoleIrpReviewer',
            headerName: 'IRP / Reviewer',
            sortable: true,
            filter: true,
            cellStyle: { ...checkboxStyle },
            cellRenderer: 'MemberCheckbox',
            cellRendererParams: {
                orgId: orgId,
            },
            flex: 1,
        });
    }
    if (isUserDetails || organization.isAnnotator) {
        columnDefs.push({
            field: 'hasRoleCurator',
            headerName: 'Curator',
            sortable: true,
            filter: true,
            cellStyle: { ...checkboxStyle },
            cellRenderer: 'MemberCheckbox',
            cellRendererParams: {
                orgId: orgId,
            },
            flex: 1,
        });
    }
    if (isUserDetails || organization.isQaReviewer) {
        columnDefs.push({
            field: 'hasRoleCuratorQc',
            headerName: 'QA Reviewer',
            sortable: true,
            filter: true,
            cellStyle: { ...checkboxStyle },
            cellRenderer: 'MemberCheckbox',
            cellRendererParams: {
                orgId: orgId,
            },
            flex: 1,
        });
    }
    if (isUserDetails || organization.isAnnotator) {
        columnDefs.push({
            field: 'hasRoleHeadCurator',
            headerName: 'Head Curator',
            sortable: true,
            filter: true,
            cellStyle: { ...checkboxStyle },
            cellRenderer: 'MemberCheckbox',
            cellRendererParams: {
                orgId: orgId,
            },
            flex: 1,
        });
    }
    /* if (!isUserDetails && organization.isQaReviewer) {
        columnDefs.push({
            field: 'hasRoleHeadCurator',
            headerName: 'Head QA Reviewer',
            sortable: false,
            filter: false,
            cellRenderer: 'MemberCheckbox',
            cellRendererParams: {
                 orgId: orgId,
            },
            flex: 1,
        });
    }*/
    if (rolesOrgSpecific) {
        rolesOrgSpecific.forEach((role) => {
            if (!defaultRoles.includes(role.code)) {
                columnDefs.push({
                    field: `hasRole${role.code}`,
                    headerName: role.name,
                    sortable: true,
                    filter: true,
                    cellStyle: { ...checkboxStyle },
                    cellRenderer: 'MemberCheckbox',
                    cellRendererParams: {
                        orgId: orgId,
                    },
                    flex: 1,
                });
            }
        });
    }

    columnDefs.push({
        field: 'isEnabled',
        headerName: 'Org Membership',
        sortable: true,
        filter: true,
        cellStyle: { ...checkboxStyle },
        cellRenderer: 'MemberCheckbox',
        cellRendererParams: {
            orgId: orgId,
            isSlider: true,
        },
        flex: 1.2,
    });
    if (!isUserDetails) {
        columnDefs.push({
            field: 'active',
            headerName: 'Account Status',
            sortable: true,
            filter: true,
            cellStyle: { ...checkboxStyle },
            flex: 1,
        });
        columnDefs.push({
            field: 'notifyAsOrgAdmin',
            headerName: 'Email Notification',
            sortable: true,
            filter: true,
            cellStyle: { ...checkboxStyle },
            cellRenderer: 'Email Notification',
            cellRendererParams: {
                orgId: orgId,
                isSlider: true,
            },
            flex: 1.2,
        });
    }

    const customComponents = [
        {
            name: 'NameRow',
            component: NameRowRendererView,
        },
        {
            name: 'Email Notification',
            component: OrgMemberCheckBoxRendererView,
        },
        {
            name: 'MemberCheckbox',
            component: OrgMemberCheckBoxRendererView,
        },
    ];

    return (
        <>
            {hasChanges && (
                <div style={{ marginBottom: Size.ROW_BOTTOM_MARGIN, color: Color.TEXT_DARK_BLUE, marginLeft: Size.HALF_PADDING }}>
                    Please press Save to save all your changes when you are done.
                </div>
            )}
            <GridView
                rowData={currentOrgMembers}
                columns={columnDefs}
                autoHeight={isUserDetails}
                hideFloatingFilter={true}
                rowHeight={50}
                customComponents={customComponents}
                rowStyle={Styles.NEW_GRID_BACKGROUND}
                onGridReady={handleOnGridReady}
            />
        </>
    );
};
